<template>
  <div class="message-center">
    <!-- 进行中的任务（显示进度条） -->
    <div class="section" v-if="progressTasks.length > 0">
      <div class="section-header">
        <span class="section-title">⏳ 进行中</span>
      </div>
      <div v-for="task in progressTasks" :key="task.taskId" class="message-card progress">
        <div class="msg-header">
          <el-tag type="warning" size="small">处理中</el-tag>
          <span class="msg-title">{{ getTaskTitle(task.taskType) }}</span>
        </div>
        <!-- 收单上传显示文件名，COC操作显示批次号 -->
        <div class="msg-content" v-if="task.fileName">{{ task.fileName }}</div>
        <div class="msg-content" v-else-if="task.bizNo">{{ task.bizNo }}</div>
        <div class="progress-bar">
          <el-progress 
            :percentage="task.progress" 
            :stroke-width="8"
          />
        </div>
        <!-- 收单上传显示阶段信息，COC操作显示处理进度 -->
        <div class="progress-stage" v-if="task.stage || task.message">
          <span>{{ task.stage }}</span>
          <span class="stage-detail" v-if="task.message">{{ task.message }}</span>
        </div>
        <div class="progress-stage" v-else-if="task.processedCount !== undefined">
          <span>已处理 {{ task.processedCount }}/{{ task.totalCount || 0 }}</span>
          <span class="stage-detail">成功 {{ task.successCount || 0 }}，失败 {{ task.failCount || 0 }}</span>
        </div>
      </div>
    </div>

    <!-- 未读消息 -->
    <div class="section" v-if="unreadMessages.length > 0">
      <div class="section-header">
        <span class="section-title">🔴 未读消息</span>
        <el-button type="primary" link size="small" @click="handleReadAll">全部已读</el-button>
      </div>
      <div v-for="msg in unreadMessages" :key="msg.id" class="message-card unread" @click="viewDetail(msg)">
        <div class="msg-header">
          <el-tag :type="msg.msgType === 'TASK_COMPLETE' ? 'success' : 'danger'" size="small">
            {{ msg.msgType === 'TASK_COMPLETE' ? '完成' : '失败' }}
          </el-tag>
          <span class="msg-title">{{ msg.title }}</span>
        </div>
        <div class="msg-content">{{ msg.content }}</div>
        <div class="msg-footer">
          <span class="msg-time">{{ formatTime(msg.createTime) }}</span>
          <el-button type="primary" link size="small" @click.stop="viewDetail(msg)">查看详情</el-button>
        </div>
      </div>
    </div>

    <!-- 已读消息 -->
    <div class="section">
      <div class="section-header">
        <span class="section-title">📗 已读消息</span>
      </div>
      <div v-if="readMessages.length === 0 && unreadMessages.length === 0 && progressTasks.length === 0" class="empty">
        暂无消息
      </div>
      <div v-for="msg in readMessages" :key="msg.id" class="message-card read" @click="viewDetail(msg)">
        <div class="msg-header">
          <el-tag :type="msg.msgType === 'TASK_COMPLETE' ? 'success' : 'danger'" size="small">
            {{ msg.msgType === 'TASK_COMPLETE' ? '完成' : '失败' }}
          </el-tag>
          <span class="msg-title">{{ msg.title }}</span>
        </div>
        <div class="msg-content">{{ msg.content }}</div>
        <div class="msg-footer">
          <span class="msg-time">{{ formatTime(msg.createTime) }}</span>
          <el-button type="primary" link size="small" @click.stop="viewDetail(msg)">查看详情</el-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { getMessages, getProcessingTasks, markAsRead, markAllAsRead } from '@/api/message'

const router = useRouter()
const messages = ref([])
const progressTasks = ref([]) // 进行中的任务进度

const unreadMessages = computed(() => messages.value.filter(m => m.isRead === 0))
const readMessages = computed(() => messages.value.filter(m => m.isRead === 1))

const emit = defineEmits(['update-count', 'close', 'progress-update'])

// 任务类型映射
const TASK_TYPE_MAP = {
  'BATCH_UPLOAD': '批量上传',
  'BATCH_REVOKE': '批量撤回',
  'BATCH_SUBMIT': '批量提交',
  'SINGLE_UPLOAD': '单条上传',
  'SINGLE_REVOKE': '单条撤回',
  'RECEIPT_UPLOAD': '收单上传',
  'COC_BATCH_CREATE': '创建COC订单批次'
}

function getTaskTitle(taskType) {
  return TASK_TYPE_MAP[taskType] || taskType
}

// 根据任务类型和进度推断默认 stage（轮询时 API 不返回 stage，避免显示过期的"准备中"）
function getDefaultStage(taskType, progress) {
  if (progress > 0) {
    // 有进度说明已在处理中
    if (taskType === 'BATCH_UPLOAD' || taskType === 'SINGLE_UPLOAD') return '正在上传'
    if (taskType === 'BATCH_SUBMIT' || taskType === 'SINGLE_SUBMIT') return '正在提交'
    if (taskType === 'BATCH_REVOKE' || taskType === 'SINGLE_REVOKE') return '正在撤回'
    if (taskType === 'COC_BATCH_CREATE') return '创建批次中'
    if (taskType === 'RECEIPT_UPLOAD') return '正在导入'
    return '处理中'
  }
  return '准备中'
}

onMounted(() => {
  loadMessages()
  // loadProcessingTasks 由父组件 openDrawer 的 setTimeout 调用
  // 如果是通过 visibilitychange 触发（抽屉已打开），也由父组件直接调用
})

async function loadMessages() {
  try {
    const res = await getMessages({ pageNum: 1, pageSize: 100 })
    messages.value = res?.records || []
    emit('update-count', unreadMessages.value.length)
  } catch (e) {
    console.error('加载消息失败', e)
  }
}

async function loadProcessingTasks() {
  try {
    const tasks = await getProcessingTasks()
    // 合并策略：对已有 SSE 实时数据的任务保留实时字段，不覆盖
    // 只用 API 数据补充新任务或缺失字段
    progressTasks.value = tasks.map(t => {
      const existing = progressTasks.value.find(p => p.taskId === t.id)
      return {
        taskId: t.id,
        taskNo: t.taskNo,
        taskType: t.taskType,
        bizNo: t.bizNo,
        // 数量字段：取 API 和 SSE 中较大的值（SSE 是实时累积的，可能比数据库更新）
        processedCount: Math.max(t.processedCount || 0, existing?.processedCount || 0),
        totalCount: Math.max(t.totalCount || 0, existing?.totalCount || 0),
        successCount: Math.max(t.successCount || 0, existing?.successCount || 0),
        failCount: Math.max(t.failCount || 0, existing?.failCount || 0),
        progress: Math.max(t.progress || 0, existing?.progress || 0),
        // SSE 独有字段：优先保留 SSE 的实时值
        fileName: existing?.fileName,
        // stage：优先 SSE 实时值；无 SSE 数据时根据 taskType 推断（避免轮询覆盖时显示"准备中"）
        stage: existing?.stage || getDefaultStage(t.taskType, t.progress),
        message: existing?.message,
      }
    })
    // 移除已不在 API 返回中的任务（已完成/已过期）
    emit('progress-update', progressTasks.value.length)
  } catch (e) {
    console.error('加载进行中任务失败', e)
  }
}

// 更新进度（从SSE推送接收）
function updateProgress(data) {
  const index = progressTasks.value.findIndex(t => t.taskId === data.taskId)
  if (index >= 0) {
    progressTasks.value[index] = { ...progressTasks.value[index], ...data }
  } else {
    progressTasks.value.push(data)
  }
  // 通知父组件进度任务数量
  emit('progress-update', progressTasks.value.length)
}

// 移除已完成任务
function removeProgressTask(taskId) {
  progressTasks.value = progressTasks.value.filter(t => t.taskId !== taskId)
  // 通知父组件更新进度数量
  emit('progress-update', progressTasks.value.length)
}

async function viewDetail(msg) {
  // 先关闭弹窗
  emit('close')
  
  // 标记已读
  if (msg.isRead === 0) {
    try {
      await markAsRead(msg.id)
      msg.isRead = 1
      emit('update-count', unreadMessages.value.length)
    } catch (e) {
      console.error('标记已读失败', e)
    }
  }

  // 跳转
  if (msg.routeName) {
    try {
      const params = typeof msg.routeParams === 'string' 
        ? JSON.parse(msg.routeParams || '{}') 
        : msg.routeParams || {}
      
      // 使用 replace 跳转
      router.replace({ name: msg.routeName, params })
    } catch (e) {
      console.error('路由跳转异常:', e)
    }
  }
}

async function handleReadAll() {
  try {
    await markAllAsRead()
    messages.value.forEach(m => m.isRead = 1)
    emit('update-count', 0)
    ElMessage.success('已全部标记为已读')
  } catch (e) {
    console.error('标记失败', e)
  }
}

function formatTime(time) {
  if (!time) return ''
  const date = new Date(time)
  const now = new Date()
  const diff = now - date
  
  if (diff < 60000) return '刚刚'
  if (diff < 3600000) return Math.floor(diff / 60000) + '分钟前'
  if (diff < 86400000) return Math.floor(diff / 3600000) + '小时前'
  return date.toLocaleDateString()
}

defineExpose({ loadMessages, loadProcessingTasks, updateProgress, removeProgressTask })
</script>

<style scoped>
.message-center {
  padding: 0 16px;
}

.section {
  margin-bottom: 20px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #ebeef5;
}

.section-title {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}

.message-card {
  padding: 12px;
  border-radius: 8px;
  background: #f5f7fa;
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.3s;
}

.message-card:hover {
  background: #ecf5ff;
}

.message-card.unread {
  background: #fff8e6;
  border-left: 3px solid #e6a23c;
}

.message-card.read {
  opacity: 0.8;
}

.msg-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.msg-title {
  font-weight: 500;
  color: #303133;
}

.msg-content {
  font-size: 13px;
  color: #606266;
  margin-bottom: 8px;
  line-height: 1.5;
}

.msg-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.msg-time {
  font-size: 12px;
  color: #909399;
}

.empty {
  text-align: center;
  color: #909399;
  padding: 40px 0;
}

.message-card.progress {
  background: #fff7e6;
  border-left: 3px solid #e6a23c;
}

.progress-bar {
  margin: 12px 0;
}

.progress-stage {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 12px;
  margin-bottom: 8px;
  color: #666;
}

.progress-stage .stage-detail {
  color: #999;
  font-size: 11px;
}
</style>
